/**
 * Audio utility for sound effects.
 * The three per-channel toggles (moves / low time / notifications) are read from
 * preferences-store (synced to backend for registered users); the two of the live
 * clock tool are device-local (`last-used-play.ts`). Calling code does not gate;
 * this module does — see `isSoundEnabled` and `CANALE_ACCESO`.
 */

import { usePreferencesStore } from '../store/preferences-store';
import { getSuonoToccoOrologio, getSuonoTempoBassoOrologio } from './last-used-play';

const audioCache = new Map<string, HTMLAudioElement>();

/**
 * Ultima chiave vista per ogni suono, per l'opzione `onceForKey` di `playSound`.
 *
 * Serve ai punti d'avvio partita che sono PIU' DI UNO per la stessa modalita':
 * in arena `resumeSearch` consuma la partita in coda e mette `playing`, e un
 * `game:start` successivo per quella stessa partita non viene scartato — la
 * guardia lascia passare proprio `playing` — quindi il suono partirebbe due
 * volte. Invece di dimostrare l'esclusivita' dei percorsi attraverso quattro
 * store, la chiave rende il doppione impossibile per costruzione.
 *
 * Si tiene solo l'ULTIMA chiave, non l'insieme: le stanze non si riusano (una
 * rivincita ne apre una nuova), quindi non c'e' niente a cui tornare.
 */
const lastKeyPerSound = new Map<string, string>();

/**
 * I canali audio che l'utente accende e spegne separatamente nelle impostazioni.
 * Ogni suono ne dichiara uno, così il gate resta un DATO: aggiungere un file qui
 * obbliga a dire da quale interruttore dipende, invece di lasciarlo suonare sempre.
 *
 * Tre sono del profilo e si sincronizzano; due sono dell'orologio dal vivo e
 * restano sul dispositivo. La differenza la porta `CANALE_ACCESO`, non questo tipo.
 */
type SoundCategory =
  | 'move'
  | 'lowTime'
  | 'notification'
  /* I due dell'orologio dal vivo (Strumenti), che NON sono i due di sopra: vedi `CANALE_ACCESO`. */
  | 'orologioTocco'
  | 'orologioBasso';

const SOUNDS: Record<string, { src: string; category: SoundCategory }> = {
  /*
   * Il nome dice «avversario trovato», ma il suono dice anche un'altra cosa: i
   * pezzi che si posano sulla damiera. E' per questo che sta bene anche dove un
   * avversario non si cerca — la sfida al computer e la partita in presenza —
   * e non e' una svista da ripulire: togliere quelle chiamate perche' «qui non
   * c'e' nessun match da trovare» rimetterebbe quelle due modalita' in
   * silenzio, che e' esattamente il difetto da cui si viene.
   */
  'match-found': { src: '/sounds/match-found.wav', category: 'notification' },
  'move': { src: '/sounds/move.wav', category: 'move' },
  'move-drag': { src: '/sounds/move-drag.wav', category: 'move' },
  'promote': { src: '/sounds/promote.wav', category: 'move' },
  'low-time': { src: '/sounds/low-time.wav', category: 'lowTime' },
  /*
   * L'orologio dal vivo riusa i due file della partita — e' lo stesso gesto
   * (un pezzo che si posa) e lo stesso avviso (il tempo che finisce) — ma con
   * un NOME proprio, perche' il nome e' cio' che porta la categoria, cioe'
   * l'interruttore. Due voci qui, non due file in `public/sounds/`.
   */
  'orologio-tocco': { src: '/sounds/move.wav', category: 'orologioTocco' },
  'orologio-basso': { src: '/sounds/low-time.wav', category: 'orologioBasso' },
};

/**
 * Da quale interruttore dipende ogni canale.
 *
 * Era una mappa categoria → CHIAVE di `UserPreferences`, e dava per scontato
 * che ogni canale fosse una preferenza sincronizzata col profilo. I due canali
 * dell'orologio dal vivo non lo sono — sono per dispositivo, e di proposito
 * (`last-used-play.ts`) — quindi la mappa porta un PREDICATO: lo stesso dato di
 * prima, con una indirezione che la rende indifferente a dove vive la risposta.
 *
 * Il valore di tutto questo e' che il cancello resta UNO. L'alternativa era una
 * seconda via audio per l'attrezzo, cioe' due posti che decidono se un suono si
 * sente: e' cosi' che nasce il tipo di difetto che questa casa insegue da tempo.
 */
const CANALE_ACCESO: Record<SoundCategory, () => boolean> = {
  move: () => usePreferencesStore.getState().moveSoundEnabled,
  lowTime: () => usePreferencesStore.getState().lowTimeSoundEnabled,
  notification: () => usePreferencesStore.getState().notificationSoundEnabled,
  orologioTocco: getSuonoToccoOrologio,
  orologioBasso: getSuonoTempoBassoOrologio,
};

function preloadSounds(): void {
  for (const [name, { src }] of Object.entries(SOUNDS)) {
    if (!audioCache.has(name)) {
      const audio = new Audio(src);
      audio.preload = 'auto';
      audioCache.set(name, audio);
    }
  }
}

preloadSounds();

/**
 * Il suono è ammesso? Un nome sconosciuto risponde `false`: non ha una categoria,
 * quindi non c'è interruttore che lo governi — e `playSound` non saprebbe comunque
 * quale file riprodurre.
 */
export function isSoundEnabled(name: string): boolean {
  const sound = SOUNDS[name];
  if (!sound) return false;
  return CANALE_ACCESO[sound.category]();
}

export function playSound(name: string, opts?: { onceForKey?: string }): void {
  if (!isSoundEnabled(name)) return;

  /*
   * La chiave si consuma PRIMA di suonare, e solo se il suono e' ammesso: due
   * chiamate per la stessa partita fanno rumore una volta sola. Chi non la
   * passa non e' toccato — i gesti espliciti (sfida al computer, partita in
   * presenza) non possono arrivare in eco e non hanno niente da deduplicare.
   */
  if (opts?.onceForKey !== undefined) {
    if (lastKeyPerSound.get(name) === opts.onceForKey) return;
    lastKeyPerSound.set(name, opts.onceForKey);
  }

  let audio = audioCache.get(name);
  if (audio) {
    audio.currentTime = 0;
  } else {
    // isSoundEnabled() ha già scartato i nomi sconosciuti: qui la voce c'è di sicuro.
    audio = new Audio(SOUNDS[name]!.src);
    audioCache.set(name, audio);
  }

  // HTMLMediaElement.play() returns a Promise in browsers but may return
  // undefined or throw in non-browser environments (e.g. jsdom).
  try {
    const result = audio.play() as unknown;
    if (result && typeof (result as Promise<void>).catch === 'function') {
      (result as Promise<void>).catch(() => {
        // Autoplay blocked - silently ignore
      });
    }
  } catch {
    // play() not implemented in this environment - silently ignore
  }
}

/**
 * How a move was entered by the local player. Only the board component knows
 * this (tap = pointerdown commit, drag = pointerup commit); the stores that
 * actually play the sound don't. Instead of threading a parameter through every
 * store action and the online-move callback chain, the board stamps the current
 * gesture here right before committing, and the sound layer reads it.
 *
 * `'auto'` non e' «il valore neutro»: vuol dire NON E' UN MIO GESTO — la mossa e'
 * comparsa sulla damiera senza che l'abbiamo posata noi (avversario online, AI,
 * sync esterna). Chi e' in quel caso lo passa esplicito e non legge questo ref.
 *
 * La premossa rigiocata NON e' fra loro, ed e' un caso che si sbaglia facilmente:
 * l'abbiamo inserita noi, col tocco o col trascinamento, e solo il momento in cui
 * viene giocata e' differito. Eredita quindi il gesto da questo ref — il ref lo
 * conserva, perche' nel frattempo l'AI passa `'auto'` come argomento senza scriverlo.
 */
export type MoveSource = 'tap' | 'drag' | 'auto';

let currentMoveSource: MoveSource = 'auto';

export function setMoveSource(source: MoveSource): void {
  currentMoveSource = source;
}

/**
 * Pick the sound key for a move. Pure — exported for unit testing.
 *
 * L'ordine delle tre regole NON e' arbitrario. La presa viene prima di tutto: una presa
 * suona SEMPRE `move`, anche trascinata, anche se promuove. Sembra togliere un segnale
 * (la damatura), ma e' il contrario — una presa non fa piu' UN suono, ne fa uno per
 * salto (vedi `playCaptureHop`), e il `promote` e' il suono dell'ULTIMO salto, che lo
 * emette da li'. Se lo emettesse anche questa funzione, il primo salto di una presa che
 * promuove suonerebbe la damatura prima ancora che la dama esista.
 *
 * Il gesto e' l'ULTIMA regola, e taglia in due posti diversi da quel che sembra: da una parte
 * il solo tocco, dall'altra tutto il resto. `move` e' il suono di un pezzo che POSIAMO noi con
 * un dito; il trascinamento e la mossa che arriva da fuori (`'auto'`) sono la stessa cosa —
 * un pezzo che compare sulla damiera senza che l'abbiamo posato — e suonano `move-drag`.
 */
export function moveSoundKey(source: MoveSource, isPromotion: boolean, isCapture = false): string {
  if (isCapture) return 'move';
  if (isPromotion) return 'promote';
  if (source === 'tap') return 'move';
  return 'move-drag'; // trascinata da noi, oppure arrivata da fuori: 'drag' e 'auto'
}

/**
 * Il suono di UN salto di presa, da emettere nel momento in cui il salto viene disegnato.
 *
 * Una presa multipla non e' un evento solo: e' una sequenza, e si sente come tale — un
 * `move.wav` ogni `CAPTURE_STEP_MS`, uno per pezzo mangiato. `promozione` vale solo per
 * l'ultimo salto, l'unico in cui la dama c'e' davvero.
 *
 * `move.wav` dura 135 ms, comodamente meno della cadenza: l'elemento in cache ha sempre
 * finito prima che gli si chieda il salto successivo.
 */
export function playCaptureHop(promozione: boolean): void {
  playSound(promozione ? 'promote' : 'move');
}

/**
 * Play the sound for a committed move, taken as a whole. `source` defaults to whatever
 * gesture the board last stamped via setMoveSource(); AI and online-opponent callers pass
 * an explicit 'auto'. Mute gating stays inside playSound().
 *
 * Per i salti di una presa multipla si usa `playCaptureHop`, non questa: qui si passa
 * `isCapture` solo perche' anche la presa di un salto solo, e la presa applicata tutta
 * in un fotogramma (premossa), devono rispettare la regola «la presa suona `move`».
 */
export function playMoveSound(
  isPromotion: boolean,
  source: MoveSource = currentMoveSource,
  isCapture = false,
): void {
  playSound(moveSoundKey(source, isPromotion, isCapture));
}
